import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { sendPushNotification } from '../sendPushNotification';
import { useTrackEvent } from '../hooks/useAnalytics';
import theme from '../theme';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

// Commissione a soglia: 10% sopra €45 di prezzo lavoratore, altrimenti €4,50 fissi
// — deve restare identica alla logica di confirm-booking-payment (Edge Function)
// e di OfferCard.jsx, altrimenti l'anteprima qui mostrata non corrisponde a
// quanto viene davvero addebitato.
const COMMISSION_RATE = 0.10;
const COMMISSION_THRESHOLD_EURO = 45;
const COMMISSION_FIXED_EURO = 4.50;

const timeSlotLabels = {
  mattina: '🌅 Mattina (8:00 - 13:00)',
  pomeriggio: '☀️ Pomeriggio (13:00 - 18:00)',
  sera: '🌙 Sera (18:00 - 22:00)',
  tutto_il_giorno: '🕐 Tutto il giorno (8:00 - 18:00)',
  da_concordare: '💬 Da concordare',
};

async function sendEmailNotification(type, to, data) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ type, to, data }),
    });
  } catch (e) {
    console.error('Errore notifica email:', e);
  }
}

function ApplicationCard({ app, session, profile, conversationId, onStatusChange }) {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const [counterPrice, setCounterPrice] = useState('');
  const [counterPriceType, setCounterPriceType] = useState('fisso');
  const [counterHours, setCounterHours] = useState('');
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const currentStatus = app.status || 'in attesa';
  const isPending = currentStatus === 'in attesa';
  const isAccepted = currentStatus === 'accettata';

  const isEmployer = app.applicant_email !== session.user.email;

  const isOra = app.proposed_price_type === 'ora';
  const rawPrice = app.proposed_price ? parseFloat(app.proposed_price) : null;
  const hours = app.proposed_hours ? parseFloat(app.proposed_hours) : null;

  const netAmount = isOra && hours && rawPrice
    ? parseFloat((rawPrice * hours).toFixed(2))
    : rawPrice;

  const priceDisplay = isOra
    ? (hours && rawPrice ? `€${rawPrice}/h × ${hours}h = €${netAmount?.toFixed(2)}` : `€${rawPrice}/h`)
    : (rawPrice ? `€${rawPrice}` : null);

  const commissionAmount = netAmount != null
    ? (netAmount >= COMMISSION_THRESHOLD_EURO ? netAmount * COMMISSION_RATE : COMMISSION_FIXED_EURO).toFixed(2)
    : null;
  const totalWithCommission = netAmount != null ? (netAmount + parseFloat(commissionAmount)).toFixed(2) : null;

  const isCounterOra = counterPriceType === 'ora';
  const counterIsValid = counterPrice && (!isCounterOra || (counterHours && parseFloat(counterHours) > 0));
  const counterTotal = isCounterOra && counterPrice && counterHours
    ? (parseFloat(counterPrice) * parseFloat(counterHours)).toFixed(2)
    : null;
  const counterPriceLabel = isCounterOra
    ? (counterTotal ? `${counterPrice}/h × ${counterHours}h = €${counterTotal}` : `${counterPrice}/h`)
    : counterPrice;

  const formatDate = (d) =>
    new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });

  const handleAccept = async () => {
    if (accepting || !isPending) return;
    setAccepting(true);
    try {
      if (!app.applicant_id) {
        alert('Errore: impossibile determinare il lavoratore. Ricarica la pagina e riprova.');
        return;
      }
      if (!netAmount) {
        alert('Questa candidatura non ha un prezzo proposto valido.');
        return;
      }

      // 1. Verifica che il committente (chi accetta e paga) abbia un metodo di pagamento valido
      const { data: customerRow } = await supabase
        .from('stripe_customers')
        .select('payment_method_validated')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (!customerRow?.payment_method_validated) {
        alert('Devi impostare un metodo di pagamento valido prima di poter accettare una candidatura.');
        navigate('/profilo');
        return;
      }

      // 1b. Verifica che il lavoratore abbia completato l'onboarding Stripe (può ricevere pagamenti)
      // Usa una funzione RPC SECURITY DEFINER perché RLS su stripe_accounts limita la SELECT
      // diretta alla propria riga: il committente deve poter controllare lo stato del lavoratore.
      const { data: workerOnboardingStatus } = await supabase
        .rpc('get_worker_onboarding_status', { worker_id: app.applicant_id });

      if (workerOnboardingStatus !== 'completato') {
        alert('Il lavoratore non ha ancora completato la configurazione per ricevere pagamenti. Non è possibile procedere.');
        return;
      }

      // 2. Blocca i fondi off-session con la carta già salvata
      const { data: paymentResult, error: paymentError } = await supabase.functions.invoke('confirm-booking-payment', {
        body: {
          client_user_id: session.user.id,
          worker_user_id: app.applicant_id,
          amount_worker_euro: netAmount,
          application_id: app.id,
        },
      });

      if (paymentError || paymentResult?.error) {
        const errCode = paymentResult?.error;
        if (errCode === 'no_payment_method') {
          alert('Devi impostare un metodo di pagamento valido prima di poter accettare una candidatura.');
          navigate('/profilo');
        } else if (errCode === 'authentication_required') {
          alert(paymentResult?.message || 'La tua banca richiede una conferma aggiuntiva. Riprova dal tuo profilo.');
        } else {
          alert('Errore nel blocco del pagamento. Riprova.');
        }
        return;
      }

      // Traccia "pagamento_bloccato" — il pagamento è stato bloccato con successo sul balance Jobbetty
      trackEvent('pagamento_bloccato', {
        conversation_id: conversationId,
        application_id: app.id,
        price: rawPrice,
        price_type: app.proposed_price_type,
        hours: isOra ? hours : null,
        amount_total: paymentResult?.amount_total ?? (totalWithCommission ? parseFloat(totalWithCommission) : null),
        source: 'application',
      });

      // 3. Pagamento bloccato con successo: aggiorna application (status + eventuali ore concordate)
      const appUpdate = isOra && hours
        ? { status: 'accettata', agreed_hours: hours }
        : { status: 'accettata' };

      const { error: appUpdateError } = await supabase.from('applications').update(appUpdate).eq('id', app.id);
      if (appUpdateError) {
        console.error('Errore aggiornamento application dopo pagamento:', appUpdateError);
        alert('Il pagamento è andato a buon fine ma c\'è stato un problema nel salvare l\'accettazione. Ricarica la pagina.');
        return;
      }

      onStatusChange(app.id, 'accettata');

      // Traccia "offerta_accettata" — percorso: accettazione diretta della prima candidatura
      trackEvent('offerta_accettata', {
        conversation_id: conversationId,
        application_id: app.id,
        price: rawPrice,
        price_type: app.proposed_price_type,
        hours: isOra ? hours : null,
        amount_total: totalWithCommission ? parseFloat(totalWithCommission) : null,
        source: 'application',
      });

      // 4. Messaggi in chat: testo di riepilogo (con scomposizione commissione) + completion_request
      // in un'unica chiamata batch, per evitare che un insert vada a buon fine e l'altro no
      const dates = app.selected_dates?.map(formatDate).join(', ') || null;
      const timeSlot = app.time_slot ? timeSlotLabels[app.time_slot] : null;
      let recapMsg = '✅ Candidatura accettata! Ecco il riepilogo:\n';
      if (dates) recapMsg += `📅 Date: ${dates}\n`;
      if (timeSlot) recapMsg += `🕐 Fascia oraria: ${timeSlot}\n`;
      if (priceDisplay) recapMsg += `💰 Prezzo proposto: ${priceDisplay}\n`;
      if (totalWithCommission) {
        recapMsg += `💳 Totale pagato: €${totalWithCommission}\n`;
        recapMsg += `   (€${netAmount.toFixed(2)} al lavoratore + €${commissionAmount} commissione Jobbetty)\n`;
      }
      recapMsg += '\nSe vuoi modificare qualcosa, scrivimi qui in chat!';

      const completionContent = isOra
        ? `🎉 Accordo raggiunto a €${rawPrice}/h per ${hours}h (€${netAmount?.toFixed(2)}) — al termine confermate entrambi il completamento`
        : `🎉 Accordo raggiunto per €${rawPrice}!`;

      const { error: messagesError } = await supabase.from('messages').insert([
        {
          conversation_id: conversationId,
          sender_id: session.user.id,
          content: recapMsg,
          read: false,
          type: 'text',
        },
        {
          conversation_id: conversationId,
          sender_id: session.user.id,
          content: completionContent,
          read: false,
          type: 'completion_request',
          offer_data: { price: rawPrice, price_type: app.proposed_price_type, hours: isOra ? hours : null },
        },
      ]);

      if (messagesError) {
        console.error('Errore invio messaggi dopo accettazione:', messagesError);
      }

      await sendEmailNotification('candidatura_accettata', app.applicant_email, {
        jobTitle: app.job_title,
        applicantName: app.applicant_email,
        employerName: profile?.full_name || session?.user?.email,
        agreedPrice: priceDisplay || null,
      });
      if (app.applicant_id) {
        sendPushNotification({
          userId: app.applicant_id,
          title: '✅ Candidatura accettata!',
          body: `${profile?.full_name || session?.user?.email} ha accettato la tua candidatura per "${app.job_title}" — pagamento bloccato`,
          url: `/chat/${conversationId}`,
        });
      }

    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    if (loading || !isPending) return;
    setLoading(true);
    try {
      await supabase.from('applications').update({ status: 'rifiutata' }).eq('id', app.id);
      onStatusChange(app.id, 'rifiutata');
      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: '❌ Mi dispiace, ho deciso di non procedere con questa candidatura.',
        read: false,
        type: 'text',
      });
      if (app.applicant_id) {
        sendPushNotification({
          userId: app.applicant_id,
          title: 'Candidatura non accettata',
          body: `${profile?.full_name || session?.user?.email} ha rifiutato la tua candidatura per "${app.job_title}"`,
          url: `/chat/${conversationId}`,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCounterOffer = async () => {
    if (loading || !isPending || !counterIsValid) return;
    setLoading(true);
    try {
      await supabase.from('applications').update({ status: 'controproposta' }).eq('id', app.id);
      onStatusChange(app.id, 'controproposta');
      await supabase.from('messages').insert([{
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: `💼 Controproposta — €${counterPriceLabel}`,
        read: false,
        type: 'offer',
        offer_data: {
          price: counterPrice,
          price_type: counterPriceType,
          hours: isCounterOra ? counterHours : null,
          dates: app.selected_dates || [],
          time_slot: app.time_slot || null,
        },
      }]);
      await sendEmailNotification('controproposta', app.applicant_email, {
        jobTitle: app.job_title,
        applicantName: app.applicant_email,
        employerName: profile?.full_name || session?.user?.email,
        counterPrice: `€${counterPriceLabel}`,
      });
      if (app.applicant_id) {
        sendPushNotification({
          userId: app.applicant_id,
          title: '💬 Nuova controproposta',
          body: `${profile?.full_name || session?.user?.email} ti ha fatto una controproposta per "${app.job_title}"`,
          url: `/chat/${conversationId}`,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = {
    accettata: { bg: theme.colors.primaryLight, color: theme.colors.primary, label: '✅ Accettata' },
    rifiutata: { bg: theme.colors.errorBg, color: theme.colors.errorDark, label: '❌ Rifiutata' },
    controproposta: { bg: theme.colors.infoBg, color: theme.colors.info, label: '💬 Controproposta inviata' },
  };

  const canAcceptNow = isEmployer && isPending && (!isOra || (hours && hours > 0));

  return (
    <div style={{
      width: '90%', margin: '0 auto 16px',
      borderRadius: theme.radius.lg, border: `2px solid ${theme.colors.primary}`,
      background: theme.colors.white, overflow: 'hidden',
      boxShadow: theme.shadow.popup,
    }}>
      {/* Header */}
      <div style={{ background: theme.colors.primary, color: theme.colors.white, padding: '10px 14px' }}>
        <div style={{ fontSize: theme.font.size.base, fontWeight: 700 }}>📋 {app.job_title}</div>
        <div style={{ fontSize: theme.font.size.xs, opacity: 0.85, marginTop: 2 }}>
          {isEmployer
            ? `Candidatura di ${app.applicant_name || app.applicant_email}`
            : 'La tua candidatura'}
        </div>
      </div>

      {/* Corpo */}
      <div style={{ padding: '12px 14px' }}>
        {priceDisplay && (
          <div style={{ background: theme.colors.primaryLight, borderRadius: theme.radius.sm, padding: '6px 10px', marginBottom: 8, fontSize: theme.font.size.base }}>
            💰 Prezzo proposto: <strong>{priceDisplay}</strong>
          </div>
        )}
        {(isPending || isAccepted) && netAmount && (
          <div style={{ background: '#f7f7f7', borderRadius: theme.radius.sm, padding: '8px 10px', marginBottom: 8, fontSize: theme.font.size.sm, color: theme.colors.textSecondary }}>
            💳 {isAccepted ? 'Totale pagato' : 'Totale da pagare'}: <strong style={{ color: theme.colors.primary }}>€{totalWithCommission}</strong>
            <div style={{ fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
              (€{netAmount.toFixed(2)} al lavoratore + €{commissionAmount} commissione Jobbetty)
            </div>
          </div>
        )}
        {app.selected_dates?.length > 0 && (
          <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: 6 }}>
            📅 {app.selected_dates.map(formatDate).join(', ')}
          </div>
        )}
        {app.time_slot && (
          <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: 6 }}>
            🕐 {timeSlotLabels[app.time_slot]}
          </div>
        )}
        {app.message && (
          <div style={{ fontSize: theme.font.size.base, color: '#555', marginBottom: 10, fontStyle: 'italic', lineHeight: 1.5 }}>
            "{app.message}"
          </div>
        )}

        {/* Bottoni — solo per committente e solo se in attesa */}
        {isEmployer && isPending && (
          <>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textSecondary, marginBottom: 6 }}>
                💰 Controproposta (opzionale)
              </div>
              <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 80 }}>
                  <span style={{
                    position: 'absolute', left: 10, top: '50%',
                    transform: 'translateY(-50%)', color: theme.colors.primary, fontWeight: 700, fontSize: theme.font.size.md
                  }}>€</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={counterPrice}
                    onChange={e => setCounterPrice(e.target.value)}
                    placeholder="Es. 20"
                    style={{
                      width: '100%', padding: '8px 12px 8px 24px',
                      borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`,
                      fontSize: theme.font.size.base, boxSizing: 'border-box'
                    }}
                  />
                </div>
                <select
                  value={counterPriceType}
                  onChange={e => { setCounterPriceType(e.target.value); if (e.target.value !== 'ora') setCounterHours(''); }}
                  style={{
                    padding: '8px 10px', borderRadius: theme.radius.sm,
                    border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.base, background: theme.colors.white
                  }}>
                  <option value="fisso">fisso</option>
                  <option value="ora">/ ora</option>
                </select>
                {isCounterOra && (
                  <input
                    type="number" min="0.5" step="0.5"
                    value={counterHours}
                    onChange={e => setCounterHours(e.target.value)}
                    placeholder="Ore"
                    style={{
                      width: 64, padding: '8px 8px',
                      borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`,
                      fontSize: theme.font.size.base, boxSizing: 'border-box'
                    }}
                  />
                )}
              </div>
              {isCounterOra && counterPrice && counterHours && (
                <div style={{ marginTop: 6, fontSize: theme.font.size.sm, color: theme.colors.primary, fontWeight: 600 }}>
                  Totale proposto: €{counterPrice} × {counterHours}h = €{counterTotal}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: theme.spacing.sm }}>
              <button
                onClick={handleAccept}
                disabled={accepting || loading || !canAcceptNow}
                style={{
                  flex: 1, background: (accepting || !canAcceptNow) ? '#ccc' : theme.colors.primary, color: theme.colors.white,
                  border: 'none', borderRadius: theme.radius.sm, padding: '9px',
                  fontSize: theme.font.size.sm, fontWeight: 700, cursor: (accepting || !canAcceptNow) ? 'not-allowed' : 'pointer'
                }}>
                {accepting ? '⏳ Blocco pagamento...' : `✅ Accetta${totalWithCommission ? ` e paga €${totalWithCommission}` : ''}`}
              </button>
              <button
                onClick={handleCounterOffer}
                disabled={loading || accepting || !counterIsValid}
                style={{
                  flex: 1,
                  background: counterIsValid ? theme.colors.info : '#ccc',
                  color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm,
                  padding: '9px', fontSize: theme.font.size.sm, fontWeight: 700,
                  cursor: counterIsValid ? 'pointer' : 'not-allowed'
                }}>
                💬 Controproposta
              </button>
              <button
                onClick={handleReject}
                disabled={loading || accepting}
                style={{
                  flex: 1, background: theme.colors.white, color: theme.colors.errorDark,
                  border: `1px solid ${theme.colors.errorDark}`, borderRadius: theme.radius.sm,
                  padding: '9px', fontSize: theme.font.size.sm, cursor: 'pointer'
                }}>
                ❌ Rifiuta
              </button>
            </div>
          </>
        )}

        {/* Stato finale */}
        {!isPending && (
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <span style={{
              background: statusConfig[currentStatus]?.bg || theme.colors.backgroundAlt,
              color: statusConfig[currentStatus]?.color || theme.colors.textSecondary,
              padding: '4px 14px', borderRadius: theme.radius.pill,
              fontSize: theme.font.size.sm, fontWeight: 600
            }}>
              {statusConfig[currentStatus]?.label || currentStatus}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApplicationCard;